import { Edit3, Plus, Trash2, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { MedicalCamp } from "../types/medicalCamp";
import { DataTable } from "./DataTable/DataTable";
import { AddNewMedicalCampModal } from "../shared/AddNewMedicalCampModal";
import { MedicalCampService } from "../services/api/medicalCamp.Service";
import toast from "react-hot-toast";
import { useAuth } from "../context/AuthContext";

export function MedicalCampView() {
  const {user} = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [showAddMedicalCampModal, setShowAddMedicalCampModal] = useState(false);

  const [medicalCampList, setMedicalCampList] = useState<MedicalCamp[]>([]);
  const [selectedCamp, setSelectedCamp] = useState<MedicalCamp | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const loadData = async () => {
      try {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        setIsLoading(false);
      } catch (error) {
        console.error("Error loading Camps:", error);
        setIsLoading(false);
      }
    };
    loadData();
  }, []);

  const loadMedicalCamps = async (page = 1, pageSize = 10, searchText = "") => {
    try {
      setIsLoading(true);
      const response = await MedicalCampService.getMedicalCamps({
        page,
        pageSize,
        searchText,
      });

      if (response.success && response.data?.dataList) {
        setCurrentPage(response.data?.currentPage);
        setTotalPages(response.data?.totalPages);
        const updatedCampData = response.data.dataList.map(
          (data: MedicalCamp) => ({
            ...data,
            campStartDate: data.campStartDate.split("T")[0],
            campEndDate: data.campEndDate.split("T")[0],
          })
        );
        setMedicalCampList(updatedCampData);
      } else {
        toast.error("No Medical Camps found");
      }
    } catch (error) {
      console.error("Error loading Medical Camps:", error);
      toast.error("Failed to load Medical Camps");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchText);
    }, 300);

    return () => clearTimeout(handler);
  }, [searchText]);

  useEffect(() => {
    loadMedicalCamps(currentPage, pageSize, searchText ?? "");
  }, [currentPage, pageSize, searchText]);

  const handleEditClick = (medicalCamp: MedicalCamp) => {
    setSelectedCamp(medicalCamp);
    setShowAddMedicalCampModal(true);
  };

  const handleDeleteMedicalCamp = async (id: string) => {
    try {
      const response = await MedicalCampService.deleteMedicalCamp(id);

      if (response.success) {
        toast.success("Medical Camp Deleted Successfully");
        loadMedicalCamps();
      } else {
        toast.error(response.message || "Failed to delete Medical Camp.");
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong");
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const columns = [
    {
      key: "campName",
      title: "Camp Name",
      sortable: true,
      width: "w-[200px]",
      render: (medicalCamp: MedicalCamp) => (
        <div>
          <div className="text-sm font-medium">{medicalCamp.campName}</div>
        </div>
      ),
    },
    {
      key: "campStartDate",
      title: "Start Date",
      sortable: true,
      width: "w-[200px]",
      render: (medicalCamp: MedicalCamp) => (
        <div>
          <div className="text-sm font-medium">{medicalCamp.campStartDate}</div>
        </div>
      ),
    },
    {
      key: "campEndDate",
      title: "End Date",
      sortable: true,
      width: "w-[200px]",
      render: (medicalCamp: MedicalCamp) => (
        <div>
          <div className="text-sm font-medium">{medicalCamp.campEndDate}</div>
        </div>
      ),
    },
    {
      key: "campSupervisorName",
      title: "Supervisor Name",
      sortable: true,
      width: "w-[200px]",
      render: (medicalCamp: MedicalCamp) => (
        <div>
          <div className="text-sm font-medium">
            {medicalCamp.campSupervisorName}
          </div>
        </div>
      ),
    },
    {
      key: "mobileNumber",
      title: "Supervisor Contact",
      sortable: true,
      width: "w-[200px]",
      render: (medicalCamp: MedicalCamp) => (
        <div>
          <div className="text-sm font-medium">{medicalCamp.mobileNumber}</div>
        </div>
      ),
    },
    {
      key: "campAddress",
      title: "Address",
      sortable: true,
      width: "w-[200px]",
      render: (medicalCamp: MedicalCamp) => (
        <div>
          <div className="text-sm  text-[#818181]">
            {medicalCamp.campAddress}, {medicalCamp.state}, {medicalCamp.city} -{" "}
            {medicalCamp.pincode}
          </div>
        </div>
      ),
    },
    {
      key: "actions",
      title: "Actions",
      width: "w-[200px]",
      render: (medicalCamp: MedicalCamp) => (
        <div className="flex items-center gap-4">
          {/* Edit */}
          <button
            className="text-green-600 hover:text-green-700"
            onClick={() => handleEditClick(medicalCamp)}
            title={`Edit details of ${medicalCamp.campName}`}
          >
            <Edit3 className="w-5 h-5" />
          </button>

          {/* Delete */}
          {/* <button
                        className="text-red-600 hover:text-red-700"
                      onClick={() => handleDeleteMedicalCamp(medicalCamp.id)}
                    title={`Delete details of ${medicalCamp.campName}`}
                    >
                        <Trash2 className="w-5 h-5" />
                    </button> */}
        </div>
      ),
    },
  ];

  const addClinicButton = (
    <button
      onClick={() => setShowAddMedicalCampModal(true)}
      className="relative z-10 flex items-center justify-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors whitespace-nowrap"
    >
      <Plus className="w-5 h-5 mr-2" />
      <span className="whitespace-nowrap">Create New Camp</span>
    </button>
  );

  const handleAddMedicalCampSuccess = () => {
    setShowAddMedicalCampModal(false);
    loadMedicalCamps();
  };

  return (
    <div className="relative">
      {!showAddMedicalCampModal && (
        <div
          className={`flex justify-end ${
            user?.role.toLocaleLowerCase() === "admin"
              ? "relative top-9"
              : user?.role.toLocaleLowerCase() === "clinic"
              ? "top-6"
              : null
          }`}
        >
          <div>
            <Search className="relative top-[2.25rem] left-4 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search camp..."
              value={searchText}
              onChange={handleSearchChange}
              className="border rounded-lg px-3 py-2.5 text-sm w-64 text-center"
            />
          </div>
        </div>
      )}

      <DataTable
        data={medicalCampList}
        columns={columns}
        itemsPerPage={10}
        searchable
        searchFields={[
          "campName",
          "mobileNumber",
          "campAddress",
          "city",
          "pincode",
          "state",
          "campSupervisorName",
          "campStartDate",
          "campEndDate",
        ]}
        isLoading={isLoading}
        onRowClick={(clinic) => console.log("clicked:", clinic)}
        pagination={{
          currentPage,
          totalPages,
          pageSize,
          setCurrentPage,
          setTotalPages,
          setPageSize,
        }}
        customFilters={addClinicButton}
      />
      {showAddMedicalCampModal && (
        <AddNewMedicalCampModal
          onClose={() => {
            setShowAddMedicalCampModal(false);
            setSelectedCamp(null);
          }}
          onSuccess={handleAddMedicalCampSuccess}
          initialData={selectedCamp}
          setInitialData={setSelectedCamp}
        />
      )}
    </div>
  );
}
